import { SCROLL_SCOPE } from "~/constants/scrollScopes";

export const SCROLL_MODULE = {
  WORK_ORDERS: "WORK_ORDERS",
  SUPPORT_TICKETS: "SUPPORT_TICKETS",
  ASSETS: "ASSETS",
  PRODUCTS: "PRODUCTS",
  COMPANIES: "COMPANIES",
  PORTALS: "PORTALS",
  PROCEDURES: "PROCEDURES",
} as const;

export type ScrollModule = (typeof SCROLL_MODULE)[keyof typeof SCROLL_MODULE];

export const SCROLL_SCOPES_BY_MODULE: Record<ScrollModule, string[]> = {
  [SCROLL_MODULE.WORK_ORDERS]: Object.values(SCROLL_SCOPE.WORK_ORDERS),
  [SCROLL_MODULE.SUPPORT_TICKETS]: Object.values(SCROLL_SCOPE.SUPPORT_TICKETS),
  [SCROLL_MODULE.ASSETS]: Object.values(SCROLL_SCOPE.ASSETS),
  [SCROLL_MODULE.PRODUCTS]: Object.values(SCROLL_SCOPE.PRODUCTS),
  [SCROLL_MODULE.COMPANIES]: Object.values(SCROLL_SCOPE.COMPANIES),
  [SCROLL_MODULE.PORTALS]: Object.values(SCROLL_SCOPE.PORTALS),
  [SCROLL_MODULE.PROCEDURES]: [
    ...Object.values(SCROLL_SCOPE.PROCEDURE_TEMPLATES),
    ...Object.values(SCROLL_SCOPE.FILLED_PROCEDURES),
  ],
};

const PROCEDURES_ACTIVE_TAB_KEY = "PROCEDURES_ACTIVE_TAB";

const VALID_PROCEDURE_TABS = ["all-templates", "filled-procedures"] as const;

export type ProceduresTabId = (typeof VALID_PROCEDURE_TABS)[number];

export const getProceduresActiveTab = (): ProceduresTabId | string =>
  sessionStorage.getItem(PROCEDURES_ACTIVE_TAB_KEY) || "all-templates";

export const setProceduresActiveTab = (tabId: string): void => {
  if (!VALID_PROCEDURE_TABS.includes(tabId as ProceduresTabId)) return;
  sessionStorage.setItem(PROCEDURES_ACTIVE_TAB_KEY, tabId);
};

export const resolveProceduresActiveTab = (
  routerState: { activeTab?: string } | null | undefined,
): ProceduresTabId | string => {
  const stateTab = routerState?.activeTab;
  if (stateTab && VALID_PROCEDURE_TABS.includes(stateTab as ProceduresTabId)) {
    return stateTab;
  }
  const persisted = getProceduresActiveTab();
  return VALID_PROCEDURE_TABS.includes(persisted as ProceduresTabId)
    ? persisted
    : "all-templates";
};

export const getProceduresListScrollScope = (): string =>
  getProceduresActiveTab() === "filled-procedures"
    ? SCROLL_SCOPE.FILLED_PROCEDURES.LIST
    : SCROLL_SCOPE.PROCEDURE_TEMPLATES.LIST;

export interface ListRouteMatchOptions {
  resolveAmbiguousListRoute?: (
    pathname: string,
    matches: ScrollRestorationRoute[],
  ) => ScrollRestorationRoute | undefined;
}

export const getProceduresListRouteMatchOptions = (
  pathname: string,
): ListRouteMatchOptions => {
  if (
    pathname === "/app/work/procedures" ||
    pathname.startsWith("/app/work/procedures?")
  ) {
    return {
      resolveAmbiguousListRoute: (_path, matches) =>
        matches.find((m) => m.scope === getProceduresListScrollScope()) ??
        matches[0],
    };
  }
  return {};
};

const SUPPORT_TICKETS_SCOPE_KEY = "SUPPORT_TICKETS_ACTIVE_SCROLL_SCOPE";
const WORK_ORDERS_SCOPE_KEY = "WORK_ORDERS_ACTIVE_SCROLL_SCOPE";
const ASSETS_SCOPE_KEY = "ASSETS_ACTIVE_SCROLL_SCOPE";
const COMPANIES_SCOPE_KEY = "COMPANIES_ACTIVE_SCROLL_SCOPE";

export const getWorkOrdersScrollScope = (): string =>
  sessionStorage.getItem(WORK_ORDERS_SCOPE_KEY) ||
  SCROLL_SCOPE.WORK_ORDERS.LIST;

export const setWorkOrdersScrollScope = (scope: string): void => {
  sessionStorage.setItem(WORK_ORDERS_SCOPE_KEY, scope);
};

export const getSupportTicketsScrollScope = (): string =>
  sessionStorage.getItem(SUPPORT_TICKETS_SCOPE_KEY) ||
  SCROLL_SCOPE.SUPPORT_TICKETS.LIST;

export const setSupportTicketsScrollScope = (scope: string): void => {
  sessionStorage.setItem(SUPPORT_TICKETS_SCOPE_KEY, scope);
};

export const getAssetsScrollScope = (): string =>
  sessionStorage.getItem(ASSETS_SCOPE_KEY) || SCROLL_SCOPE.ASSETS.LIST;

export const setAssetsScrollScope = (scope: string): void => {
  sessionStorage.setItem(ASSETS_SCOPE_KEY, scope);
};

export const getCompaniesScrollScope = (): string =>
  sessionStorage.getItem(COMPANIES_SCOPE_KEY) || SCROLL_SCOPE.COMPANIES.LIST;

export const setCompaniesScrollScope = (scope: string): void => {
  sessionStorage.setItem(COMPANIES_SCOPE_KEY, scope);
};

const PRODUCTS_SCOPE_KEY = "PRODUCTS_ACTIVE_SCROLL_SCOPE";

export const getProductsScrollScope = (): string =>
  sessionStorage.getItem(PRODUCTS_SCOPE_KEY) || SCROLL_SCOPE.PRODUCTS.LIST;

export const setProductsScrollScope = (scope: string): void => {
  sessionStorage.setItem(PRODUCTS_SCOPE_KEY, scope);
};

export interface ScrollRestorationRoute {
  module: ScrollModule;
  listPath: string;
  listPaths?: string[];
  scope: string;
  detailPattern: RegExp;
  resolveScope?: (pathname: string) => string;
}

export const SCROLL_RESTORATION_ROUTES: ScrollRestorationRoute[] = [
  {
    module: SCROLL_MODULE.WORK_ORDERS,
    listPath: "/app/work/work-orders",
    scope: SCROLL_SCOPE.WORK_ORDERS.LIST,
    detailPattern: /^\/app\/work\/work-orders\/[^/]+$/,
    resolveScope: () => getWorkOrdersScrollScope(),
  },
  {
    module: SCROLL_MODULE.WORK_ORDERS,
    listPath: "/app/work/archived-work-orders",
    scope: SCROLL_SCOPE.WORK_ORDERS.LIST,
    detailPattern: /^\/app\/work\/work-orders\/[^/]+$/,
    resolveScope: () => getWorkOrdersScrollScope(),
  },
  {
    module: SCROLL_MODULE.SUPPORT_TICKETS,
    listPath: "/app/work/tickets",
    scope: SCROLL_SCOPE.SUPPORT_TICKETS.LIST,
    detailPattern: /^\/app\/work\/tickets\/[^/]+$/,
    resolveScope: () => getSupportTicketsScrollScope(),
  },
  {
    module: SCROLL_MODULE.ASSETS,
    listPath: "/app/asset-hub/my-assets",
    listPaths: ["/app/asset-hub/my-assets", "/app/asset-hub"],
    scope: SCROLL_SCOPE.ASSETS.LIST,
    detailPattern:
      /^\/app\/(?:asset-hub\/my-assets|assets\/(?:all|machines))\/[^/]+$/,
    resolveScope: () => getAssetsScrollScope(),
  },
  {
    module: SCROLL_MODULE.PRODUCTS,
    listPath: "/app/asset-hub/my-products",
    scope: SCROLL_SCOPE.PRODUCTS.LIST,
    detailPattern: /^\/app\/asset-hub\/my-products\/[^/]+$/,
    resolveScope: () => getProductsScrollScope(),
  },
  {
    module: SCROLL_MODULE.COMPANIES,
    listPath: "/app/asset-hub/companies",
    listPaths: [
      "/app/asset-hub/companies",
      // CMMS accounts browse the same module under /locations.
      "/app/asset-hub/locations",
      "/app/asset-hub/connections",
    ],
    scope: SCROLL_SCOPE.COMPANIES.LIST,
    detailPattern:
      /^\/app\/(?:asset-hub\/(?:companies|locations|connections)|facilities)\/[^/]+$/,
    resolveScope: () => getCompaniesScrollScope(),
  },
  {
    module: SCROLL_MODULE.PORTALS,
    listPath: "/app/customer-portal/my-portals",
    scope: SCROLL_SCOPE.PORTALS.LIST,
    detailPattern: /^\/app\/customer-portal\/my-portals\/[^/]+$/,
  },
  {
    module: SCROLL_MODULE.PROCEDURES,
    listPath: "/app/work/procedures",
    scope: SCROLL_SCOPE.FILLED_PROCEDURES.LIST,
    detailPattern: /^\/app\/work\/procedures\/instance\/[^/]+$/,
    resolveScope: () => SCROLL_SCOPE.FILLED_PROCEDURES.LIST,
  },
  {
    module: SCROLL_MODULE.PROCEDURES,
    listPath: "/app/work/procedures",
    scope: SCROLL_SCOPE.PROCEDURE_TEMPLATES.LIST,
    detailPattern: /^\/app\/work\/procedures\/(?!instance)[^/]+$/,
    resolveScope: () => getProceduresListScrollScope(),
  },
];
